import { FollowUpActionStatus, GoalStatus } from '@lmc/ts-common'
import {
  Button,
  Handler,
  InlineEdit,
  InputSelect,
  ReorderableItem,
  ReorderableItemProps,
} from '@lmc/ui-kit'
import { Form } from 'antd'
import { NAMESPACE } from 'locale'
import { goalsConstants } from 'locale/goals'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { hasActionPlanTitleError } from '../../utils'
import { useState } from 'react'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Trash } from '@lmc/ui-kit'

export type ActionPlanStatus = GoalStatus | FollowUpActionStatus

export type ActionPlanItemValue<T extends ActionPlanStatus> = {
  /**
   * this is the id used for ReorderableList
   * this is not the DB ID
   */
  id: ReorderableItemProps['id']
  title: string
  status: T
  itemStoredID: ReorderableItemProps['id'] | undefined
  canDelete?: boolean
  canEdit?: boolean
}

export type ActionPlanItemStatusOption<T extends ActionPlanStatus> = {
  value: T
  label: string
}

export interface ActionPlanItemProps<T extends ActionPlanStatus>
  extends ReorderableItemProps,
    ActionPlanItemValue<T> {
  onChange: (value: ActionPlanItemValue<T>) => void
  onRemove: (id: string | number) => void
  disabled?: boolean
  options: ActionPlanItemStatusOption<T>[]
  defaultValue: T
}

export const ACTION_PLAN_ITEM_DELETE_ID = 'ACTION_PLAN_ITEM_DELETE_ID'

const ActionPlanItem = <T extends ActionPlanStatus>(
  props: ActionPlanItemProps<T>
) => {
  const {
    disabled,
    title,
    onChange,
    id,
    onRemove,
    itemStoredID,
    status,
    options,
    defaultValue,
    canDelete = true,
    canEdit = true,
    ...reorderableItemSpecificProps
  } = props
  const { t } = useTranslation(NAMESPACE.GOALS)
  const [hasBeenChanged, setHasBeenChanged] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  const titleChangeHandler = (value: string) => {
    const newValue: ActionPlanItemValue<T> = {
      id,
      title: value,
      status,
      itemStoredID,
      canDelete,
      canEdit,
    }

    setHasBeenChanged(true)

    onChange(newValue)
  }

  const statusChangeHandler = (value: T) => {
    const newValue: ActionPlanItemValue<T> = {
      id,
      title,
      status: value,
      itemStoredID,
      canDelete,
      canEdit,
    }

    onChange(newValue)
  }

  const hasError = hasBeenChanged && hasActionPlanTitleError(title)
  const errorMessage = t(goalsConstants.ADD_TITLE_ACTION_ERROR)

  const itemValidateStatus = hasError ? 'error' : undefined
  const helpMessage = hasError ? errorMessage : undefined

  return (
    <ReorderableItem handler id={id} {...reorderableItemSpecificProps}>
      <Wrapper>
        <HandlerStyled id={id} />
        <ContentWrapper>
          <Form.Item validateStatus={itemValidateStatus} help={helpMessage}>
            <InlineEditStyled
              defaultEditState={title === ''}
              disabled={disabled || !canEdit}
              onChange={titleChangeHandler}
              value={title}
              isEditing={hasError ? true : undefined}
              displayActions={false}
              setIsEditing={setIsEditing}
            />
          </Form.Item>
          <RightContentWrapper>
            {disabled || (
              <>
                <InputSelectStyled
                  onChange={(value) => statusChangeHandler(value)}
                  options={options}
                  defaultValue={status || defaultValue}
                />
                {canDelete && !isEditing ? (
                  <ConfirmationModal>
                    <ButtonStyled onClick={() => onRemove(id)} variant="text">
                      <DeleteOutlinedStyled
                        data-testid={ACTION_PLAN_ITEM_DELETE_ID}
                      />
                    </ButtonStyled>
                  </ConfirmationModal>
                ) : (
                  <DeleteSpace />
                )}
              </>
            )}
          </RightContentWrapper>
        </ContentWrapper>
      </Wrapper>
    </ReorderableItem>
  )
}

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.color.neutral.color10};
  border-radius: 15px;
  padding: 16px 24px;
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  gap: 8px;
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`
const RightContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  gap: 18px;
  justify-content: end;
  align-items: center;
`
const HandlerStyled = styled(Handler)`
  height: 100%;
`

const InlineEditStyled = styled(InlineEdit)`
  max-width: 596px;
  width: 100%;
`
const DeleteOutlinedStyled = styled(Trash)`
  cursor: pointer;
  width: 20px;
  height: 20px;

  color: ${({ theme }) => theme.color.neutral.color5};
  :hover {
    color: ${({ theme }) => theme.color.status.danger};
  }
`

const ButtonStyled = styled(Button)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const DeleteSpace = styled.div`
  width: 20px;
  height: 20px;
`

const InputSelectStyled = styled(InputSelect)`
  flex: 1;
  max-width: 150px;
`

export default ActionPlanItem
